import Reveal from "@/components/Reveal";
import Tilt3D from "@/components/Tilt3D";
import { TID } from "@/lib/testIds";
import { Shield, Star, HeartPulse, Landmark, BadgeCheck, Swords } from "lucide-react";
import { useSiteContent, resolveImg } from "@/admin/SiteContent";

// Default fallback used when the CMS payload is empty (very first boot).
// The live site should always read from `content.factions`.
const FALLBACK = [
  {
    key: "lspd",
    name: "LSPD",
    role: "Los Santos Police Department",
    desc: "Polizia metropolitana di Los Santos. Pattuglie urbane, investigazioni, unità tattiche e protocolli di intervento.",
  },
  {
    key: "fbi",
    name: "FBI",
    role: "Federal Bureau of Investigation",
    desc: "Unità federale per crimini organizzati, casi ad alto profilo, intelligence e operazioni sotto copertura.",
  },
  {
    key: "ems",
    name: "EMS",
    role: "Emergency Medical Service",
    desc: "Pronto soccorso, ospedale, chirurgia e psichiatria. Cura, salva, riporta in scena.",
  },
];

// Pick a sensible fallback icon based on the faction key/name so that
// factions without an uploaded logo still get a themed pictogram.
const pickIcon = (key = "", name = "") => {
  const h = `${key} ${name}`.toLowerCase();
  if (h.includes("lspd") || h.includes("police") || h.includes("polizia")) return Shield;
  if (h.includes("fbi") || h.includes("bureau")) return Star;
  if (h.includes("ems") || h.includes("medic") || h.includes("emergency")) return HeartPulse;
  if (h.includes("gov") || h.includes("istituzion")) return Landmark;
  if (h.includes("bcso") || h.includes("sheriff")) return BadgeCheck;
  if (h.includes("milit") || h.includes("army") || h.includes("devgru")) return Swords;
  return Shield;
};

// Best-effort role subtitle: prefer explicit `role`, then `code`.
const pickRole = (f) => {
  if (f.role) return f.role;
  if (f.code) return `Codice · ${f.code}`;
  return "";
};

export const Factions = () => {
  const { content } = useSiteContent();

  // While loading (content === null) we render nothing to avoid a flash
  // of the stale FALLBACK list. Only if the fetch actually failed
  // (content === false) or came back empty do we degrade to the fallback.
  if (content === null) return null;

  const raw = (content && content.factions && content.factions.length > 0)
    ? content.factions
    : FALLBACK;
  const items = raw.map((f, i) => ({ ...f, key: f.key || `faction-${i}` }));

  return (
    <section
      id="factions"
      data-testid={TID.factions.root}
      className="relative py-24 sm:py-32"
    >
      {/* Subtle marquee header */}
      <div className="absolute top-10 inset-x-0 pointer-events-none select-none opacity-[0.05]">
        <div className="marquee">
          <div className="marquee__track font-gaming text-[10vw] leading-none text-[#D4AF37] whitespace-nowrap">
            <span>FORZE DELL&apos;ORDINE · LEGGE · ORDINE ·</span>
            <span>FORZE DELL&apos;ORDINE · LEGGE · ORDINE ·</span>
            <span>FORZE DELL&apos;ORDINE · LEGGE · ORDINE ·</span>
          </div>
        </div>
      </div>

      <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
        <div className="grid lg:grid-cols-12 gap-10 mb-16">
          <div className="lg:col-span-6">
            <Reveal>
              <div className="flex items-center gap-3 mb-5">
                <span className="h-px w-10 bg-[#D4AF37]/60" />
                <span className="font-mono-pl text-[10px] uppercase tracking-[0.32em] text-[#D4AF37]">
                  Fazioni Ufficiali
                </span>
              </div>
            </Reveal>
            <Reveal delay={1}>
              <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl uppercase text-[#F5F5F5] leading-[0.98] tracking-wide">
                Forze dell&apos;<span className="text-gold glow-gold">Ordine</span>
              </h2>
            </Reveal>
          </div>
          <div className="lg:col-span-6 lg:pt-4">
            <Reveal delay={2}>
              <p className="text-[#B6B6BE] text-base sm:text-lg leading-relaxed">
                I corpi ufficiali che tengono Los Santos in piedi. Pattuglie,
                emergenze, indagini federali e istituzioni civili — il cuore
                legale del server PURELIFE Roleplay.
              </p>
            </Reveal>
          </div>
        </div>

        {/* Responsive grid — auto-fits any number of factions from CMS */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((f, i) => {
            const Icon = pickIcon(f.key, f.name);
            const role = pickRole(f);
            return (
              <Reveal
                key={f.key}
                delay={Math.min((i % 5) + 1, 5)}
              >
                <Tilt3D max={6} scale={1.01} className="relative h-full rounded-2xl">
                  <article
                    data-testid={TID.factions.item(f.key)}
                    className="card-wow card-wow--gold rounded-2xl p-7 h-full flex flex-col justify-between min-h-[240px]"
                  >
                  <div>
                    <div className="flex items-start justify-between gap-3">
                      <div className="w-14 h-14 rounded-xl bg-[#0B0B0F]/60 border border-[#D4AF37]/30 flex items-center justify-center overflow-hidden shrink-0">
                        {f.image_url ? (
                          <img
                            src={resolveImg(f.image_url)}
                            alt={f.name}
                            className="w-full h-full object-contain p-1.5"
                            data-testid={`faction-logo-${f.key}`}
                          />
                        ) : (
                          <Icon size={22} className="text-[#D4AF37]" strokeWidth={1.6} />
                        )}
                      </div>
                      <span className="text-[9px] tracking-[0.28em] uppercase px-2.5 py-1 rounded-full border border-[#00D4FF]/30 text-[#00D4FF] bg-[#0B0B0F]/40">
                        Legal
                      </span>
                    </div>
                    <h3 className="mt-8 font-display text-2xl sm:text-3xl uppercase text-[#F5F5F5] tracking-wide">
                      {f.name}
                    </h3>
                    {role && (
                      <p className="text-[10px] uppercase tracking-[0.22em] text-[#B6B6BE] mt-1.5">
                        {role}
                      </p>
                    )}
                  </div>
                  <p className="text-sm text-[#B6B6BE] mt-6 leading-relaxed">
                    {f.desc}
                  </p>
                  </article>
                </Tilt3D>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Factions;
